<template>
  <div class="theme-toggle">
    <!-- 主题模式切换 -->
    <ElDropdown
      trigger="click"
      placement="bottom-end"
      @command="handleThemeCommand"
    >
      <ElButton
        :icon="currentThemeIcon"
        circle
        :title="`当前主题: ${currentThemeText}`"
        class="theme-toggle-btn"
      />
      <template #dropdown>
        <ElDropdownMenu>
          <ElDropdownItem
            command="light"
            :class="{ 'is-active': appStore.settings.themeMode === 'light' }"
          >
            <ElIcon><Sunny /></ElIcon>
            <span>浅色模式</span>
          </ElDropdownItem>
          <ElDropdownItem
            command="dark"
            :class="{ 'is-active': appStore.settings.themeMode === 'dark' }"
          >
            <ElIcon><Moon /></ElIcon>
            <span>深色模式</span>
          </ElDropdownItem>
          <ElDropdownItem
            command="auto"
            :class="{ 'is-active': appStore.settings.themeMode === 'auto' }"
          >
            <ElIcon><Monitor /></ElIcon>
            <span>跟随系统</span>
          </ElDropdownItem>
          <ElDropdownItem divided command="color-picker">
            <ElIcon><Brush /></ElIcon>
            <span>主题色设置</span>
          </ElDropdownItem>
        </ElDropdownMenu>
      </template>
    </ElDropdown>

    <!-- 主题色选择器弹窗 -->
    <ElDialog
      v-model="showColorPicker"
      title="主题色设置"
      width="400px"
      :before-close="handleColorPickerClose"
    >
      <div class="color-picker-content">
        <!-- 预设主题色 -->
        <div class="preset-colors">
          <h4 class="section-title">预设主题色</h4>
          <div class="color-grid">
            <div
              v-for="color in presetColors"
              :key="color.name"
              class="color-item"
              :class="{ 'is-active': appStore.settings.primaryColor === color.value }"
              :title="color.name"
              @click="selectPresetColor(color.value)"
            >
              <div
                class="color-block"
                :style="{ backgroundColor: color.value }"
              >
                <ElIcon v-if="appStore.settings.primaryColor === color.value" class="check-icon">
                  <Check />
                </ElIcon>
              </div>
              <span class="color-name">{{ color.name }}</span>
            </div>
          </div>
        </div>

        <!-- 自定义颜色 -->
        <div class="custom-color">
          <h4 class="section-title">自定义颜色</h4>
          <div class="color-picker-wrapper">
            <ElColorPicker
              v-model="customColor"
              :predefine="presetColorValues"
              show-alpha
              size="large"
              @change="handleCustomColorChange"
            />
            <div class="color-info">
              <span class="color-value">{{ customColor || appStore.settings.primaryColor }}</span>
            </div>
          </div>
        </div>

        <!-- 深色模式限制提示 -->
        <div v-if="appStore.isDark" class="dark-mode-notice">
          <ElAlert
            title="深色模式下主题色自定义功能受限"
            type="info"
            :closable="false"
            show-icon
          >
            <template #default>
              <p>根据设计规范，深色模式下为了保证视觉一致性和可读性，主题色的自定义范围会受到限制。</p>
            </template>
          </ElAlert>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <ElButton @click="resetToDefault">重置默认</ElButton>
          <ElButton @click="showColorPicker = false">取消</ElButton>
          <ElButton type="primary" @click="applyColorChanges">应用</ElButton>
        </div>
      </template>
    </ElDialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAppStore } from '@/stores/app'
import {
  Sunny,
  Moon,
  Monitor,
  Brush,
  Check
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import type { ThemeMode } from '@/stores/app'

// 状态管理
const appStore = useAppStore()

// 响应式数据
const showColorPicker = ref(false)
const customColor = ref('')

// 预设主题色
const presetColors = [
  { name: '拂晓蓝', value: '#1890ff' },
  { name: '薄暮', value: '#722ed1' },
  { name: '火山', value: '#fa541c' },
  { name: '日暮', value: '#faad14' },
  { name: '明青', value: '#13c2c2' },
  { name: '极光绿', value: '#52c41a' },
  { name: '酱紫', value: '#722ed1' },
  { name: '胭脂', value: '#eb2f96' },
  { name: '科技蓝', value: '#3b82f6' },
  { name: '商务蓝', value: '#1e40af' },
  { name: '活力橙', value: '#f97316' },
  { name: '自然绿', value: '#059669' }
]

// 预设颜色值数组（用于颜色选择器）
const presetColorValues = computed(() =>
  presetColors.map(color => color.value)
)

// 计算属性
const currentThemeIcon = computed(() => {
  switch (appStore.settings.themeMode) {
    case 'light':
      return Sunny
    case 'dark':
      return Moon
    case 'auto':
      return Monitor
    default:
      return Sunny
  }
})

const currentThemeText = computed(() => {
  switch (appStore.settings.themeMode) {
    case 'light':
      return '浅色模式'
    case 'dark':
      return '深色模式'
    case 'auto':
      return '跟随系统'
    default:
      return '浅色模式'
  }
})

// 方法
const handleThemeCommand = (command: string) => {
  if (command === 'color-picker') {
    showColorPicker.value = true
    customColor.value = appStore.settings.primaryColor
  } else {
    appStore.setThemeMode(command as ThemeMode)
    ElMessage.success(`已切换到${getThemeText(command as ThemeMode)}`)
  }
}

const getThemeText = (mode: ThemeMode): string => {
  const texts = {
    light: '浅色模式',
    dark: '深色模式',
    auto: '跟随系统模式'
  }
  return texts[mode]
}

const selectPresetColor = (color: string) => {
  customColor.value = color
}

const handleCustomColorChange = (color: string) => {
  customColor.value = color
}

const applyColorChanges = () => {
  if (customColor.value) {
    appStore.setPrimaryColor(customColor.value)
    ElMessage.success('主题色已更新')
  }
  showColorPicker.value = false
}

const resetToDefault = () => {
  const defaultColor = '#3b82f6'
  customColor.value = defaultColor
  appStore.setPrimaryColor(defaultColor)
  ElMessage.success('已重置为默认主题色')
}

const handleColorPickerClose = () => {
  showColorPicker.value = false
  customColor.value = appStore.settings.primaryColor
}

// 监听主题变化
watch(
  () => appStore.settings.primaryColor,
  (newColor) => {
    customColor.value = newColor
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
.theme-toggle {
  .theme-toggle-btn {
    border: none;
    background: transparent;
    color: var(--el-text-color-primary);

    &:hover {
      background: var(--el-fill-color-light);
    }

    // 设置主题切换按钮图标字体大小
    :deep(.el-icon) {
      font-size: 20px !important;
    }
  }
}

.color-picker-content {
  .section-title {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 500;
    color: var(--el-text-color-primary);
  }

  .preset-colors {
    margin-bottom: 24px;

    .color-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;

      .color-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        padding: 8px;
        border-radius: 6px;
        transition: all 0.2s ease;

        &:hover {
          background: var(--el-fill-color-light);
        }

        &.is-active {
          background: var(--el-color-primary-light-9);
        }

        .color-block {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 2px solid var(--el-border-color-light);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;

          .check-icon {
            color: white;
            font-size: 16px;
            text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
          }
        }

        .color-name {
          font-size: 12px;
          color: var(--el-text-color-regular);
          text-align: center;
        }
      }
    }
  }

  .custom-color {
    margin-bottom: 24px;

    .color-picker-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;

      .color-info {
        .color-value {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 12px;
          color: var(--el-text-color-secondary);
          background: var(--el-fill-color-light);
          padding: 4px 8px;
          border-radius: 4px;
        }
      }
    }
  }

  .dark-mode-notice {
    margin-top: 16px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

// Element Plus 下拉菜单项激活状态
:deep(.el-dropdown-menu__item.is-active) {
  background-color: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}

// 深色模式适配
.dark {
  .theme-toggle-btn {
    &:hover {
      background: var(--el-fill-color-dark);
    }
  }

  .color-item {
    &:hover {
      background: var(--el-fill-color-dark);
    }

    &.is-active {
      background: var(--el-color-primary-dark-2);
    }
  }
}
</style>
